import { ErrorRetry } from "@/features/common/components/ErrorRetry";
import { Title } from "@/features/common/components/Title";
import { FilterSideBar } from "@/features/common/components/filter/FilterSideBar";
import { FilterStagger } from "@/features/common/components/filter/FilterStagger";
import { EmptySearchResult } from "@/features/job/components/jobList/EmptySearchResult";
import { JobCard } from "@/features/job/components/jobList/JobCard";
import { JobListFilterRow } from "@/features/job/components/jobList/JobListFilterRow";
import { LoadingJobList } from "@/features/job/components/jobList/LoadingJobList";
import {
  desktopJobListFilterList,
  mobileJobListFilterList,
} from "@/features/job/const/jobListFilterList";
import { useJobDetailsDialog } from "@/features/job/hooks/useJobDetailsDialog";
import {
  JobOrderByWithRelationInput,
  JobWhereInput,
  useInfiniteJobsQuery
} from "@/generated/graphql-hooks";
import { useMemo } from "react";
import InfiniteScroll from "react-infinite-scroller";

const itemsPerPage = 12;

type JobListProps = {
  count?: number;
  where?: JobWhereInput;
  orderBy?: Array<JobOrderByWithRelationInput> | JobOrderByWithRelationInput;
};

export default function JobList({ count, where, orderBy }: JobListProps) {
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useInfiniteJobsQuery(
    "where",
    {
      where,
      take: itemsPerPage,
      orderBy
    },
    {
      getNextPageParam: (lastPage) => {
        if ((lastPage?.jobs?.length ?? 0) < itemsPerPage) {
          return false;
        }

        return {
          cursor: {
            id: lastPage.jobs?.[lastPage.jobs.length - 1]?.id,
          },
        };
      },
    }
  );

  const jobs = useMemo(() => {
    return (data?.pages ?? []).map(item => item.jobs).flat();
  }, [data]);

  useJobDetailsDialog({ jobs, isLoading });

  return (
    <>
      <FilterSideBar
        className="hidden md:block"
        filterList={desktopJobListFilterList}
      />
      <FilterStagger
        className="md:hidden"
        filterList={mobileJobListFilterList}
      />

      <section className="col-span-3 mt-6 space-y-4 sm:space-y-6 lg:mt-0">
        <div className="flex items-center justify-between text-base">
          <div className="flex items-end space-x-2">
            <Title size="h2" className="font-normal text-gray-900">
              通告欄
            </Title>
            <div className="text-gray-700">/ {count}份通告</div>
          </div>
        </div>

        <div className="relative flex items-center justify-end sm:justify-between">
          <JobListFilterRow className="hidden md:flex" />
        </div>

        <div className="space-y-3">
          {!isLoading && !!error && <ErrorRetry refetch={refetch} error={error}/>}

          {!isLoading && !jobs?.length && !error && <EmptySearchResult />}

          <InfiniteScroll
            pageStart={1}
            loadMore={() => !isFetching && !isFetchingNextPage && fetchNextPage()}
            hasMore={hasNextPage}
            loader={
              <div className="space-y-3">
                <LoadingJobList />
              </div>
            }
          >
            <div className="flex flex-col gap-2">
              {jobs.map((job) => {
                return <JobCard job={job} key={job.id} />
              })}
            </div>
          </InfiniteScroll>
        </div>
      </section>
    </>
  );
}
